import {
  Contract,
  Milestone,
  Amendment,
  ContractWithUsers,
  UserInfo,
} from '../types/contract';
import { DEFAULT_AVATAR_URL } from '../constants/assets';

// Mock Users
export const mockUsers: Record<string, UserInfo> = {
  client1: {
    user_id: 'client1',
    nickname: '云端科技',
    avatar: DEFAULT_AVATAR_URL,
    role: 'Client',
  },
  client2: {
    user_id: 'client2',
    nickname: '星辰传媒',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&q=80',
    role: 'Client',
  },
  creator1: {
    user_id: 'creator1',
    nickname: '光影造梦师',
    avatar: 'https://images.unsplash.com/photo-1527980965255-d3b416303d12?auto=format&fit=crop&w=100&q=80',
    role: 'Sora Specialist',
  },
  creator2: {
    user_id: 'creator2',
    nickname: 'PixelAlchemist',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=100&q=80',
    role: 'Runway Director',
  },
  creator3: {
    user_id: 'creator3',
    nickname: '云端叙事',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&q=80',
    role: 'Midjourney Artist',
  },
  currentUser: {
    user_id: 'currentUser',
    nickname: '当前用户',
    avatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?auto=format&fit=crop&w=100&q=80',
    role: 'User',
  },
};

// Helper to get timestamps
const now = Date.now();
const daysAgo = (days: number) => now - days * 24 * 60 * 60 * 1000;
const daysFromNow = (days: number) => now + days * 24 * 60 * 60 * 1000;

// Mock Contracts
export const mockContracts: Contract[] = [
  // Contract 1: Pending Client Approval (creator initiated)
  {
    contract_id: 'contract_001',
    brief_id: 'brief_123',
    client_user_id: 'currentUser',
    creator_user_id: 'creator1',
    title: '高端护肤品牌 AIGC 概念短片制作',
    description: '为新品发布会制作一支30秒的AIGC概念短片，展现产品的科技感和未来感。',
    requirements: '1. 30秒视频长度\n2. 4K分辨率\n3. 包含产品特写镜头\n4. 科技感配乐\n5. 提供源文件',
    terms: '1. 项目周期为15个工作日\n2. 分3个里程碑交付\n3. 每个里程碑需客户确认后进入下一阶段\n4. 最多允许2次修改\n5. 版权归客户所有',
    total_amount: 25000,
    currency: 'CNY',
    estimated_delivery_date: daysFromNow(15),
    status: 'PENDING_CLIENT_APPROVAL',
    creator_signed_at: daysAgo(1),
    created_at: daysAgo(1),
    updated_at: daysAgo(1),
  },
  // Contract 2: Active with milestones in various states
  {
    contract_id: 'contract_002',
    client_user_id: 'currentUser',
    creator_user_id: 'creator2',
    title: '电商产品宣传视频制作',
    description: '制作3支电商产品宣传短视频，用于社交媒体推广。',
    requirements: '1. 每支视频15秒\n2. 竖屏格式 1080x1920\n3. 动感节奏\n4. 突出产品卖点\n5. 适合抖音/小红书投放',
    terms: '1. 总周期10个工作日\n2. 按视频数量分3个里程碑\n3. 支持无限次修改\n4. 提供可编辑源文件',
    total_amount: 12000,
    currency: 'CNY',
    estimated_delivery_date: daysFromNow(10),
    status: 'ACTIVE',
    client_signed_at: daysAgo(8),
    creator_signed_at: daysAgo(7),
    created_at: daysAgo(8),
    updated_at: daysAgo(1),
  },
  // Contract 3: Active, as creator
  {
    contract_id: 'contract_003',
    client_user_id: 'client1',
    creator_user_id: 'currentUser',
    title: '科幻短剧场景概念设计',
    description: '为科幻短剧《星际迷航》设计5个关键场景的概念图。',
    requirements: '1. 5个场景概念图\n2. 每个场景3个角度\n3. 高精度渲染\n4. 包含场景说明\n5. 符合剧本设定',
    terms: '1. 项目周期20个工作日\n2. 分阶段提交\n3. 客户可提出修改意见\n4. 最终版权归客户',
    total_amount: 18000,
    currency: 'CNY',
    estimated_delivery_date: daysFromNow(12),
    status: 'ACTIVE',
    client_signed_at: daysAgo(5),
    creator_signed_at: daysAgo(5),
    created_at: daysAgo(5),
    updated_at: daysAgo(2),
  },
  // Contract 4: Completed
  {
    contract_id: 'contract_004',
    brief_id: 'brief_456',
    client_user_id: 'currentUser',
    creator_user_id: 'creator3',
    title: '企业宣传片 AIGC 制作',
    description: '制作3分钟企业形象宣传片。',
    requirements: '1. 3分钟时长\n2. 1080P高清\n3. 中英文字幕\n4. 配音配乐\n5. 企业文化展示',
    terms: '1. 30天制作周期\n2. 分5个里程碑交付\n3. 版权买断',
    total_amount: 45000,
    currency: 'CNY',
    estimated_delivery_date: daysAgo(2),
    status: 'COMPLETED',
    client_signed_at: daysAgo(32),
    creator_signed_at: daysAgo(32),
    completed_at: daysAgo(2),
    created_at: daysAgo(32),
    updated_at: daysAgo(2),
  },
  // Contract 5: Cancelled
  {
    contract_id: 'contract_005',
    client_user_id: 'client2',
    creator_user_id: 'currentUser',
    title: '游戏角色 3D 建模',
    description: '为手游设计并建模3个角色。',
    requirements: '1. 3个游戏角色\n2. 低多边形风格\n3. 带动作绑定\n4. Unity兼容',
    terms: '1. 45天周期\n2. 角色逐个交付',
    total_amount: 30000,
    currency: 'CNY',
    estimated_delivery_date: daysFromNow(-10),
    status: 'CANCELLED',
    client_signed_at: daysAgo(20),
    creator_signed_at: daysAgo(20),
    cancelled_at: daysAgo(15),
    cancelled_by: 'client2',
    cancellation_reason: '项目需求变更，暂停制作',
    created_at: daysAgo(20),
    updated_at: daysAgo(15),
  },
  // Contract 6: Another pending for client approval (creator initiated)
  {
    contract_id: 'contract_006',
    client_user_id: 'client1',
    creator_user_id: 'currentUser',
    title: '产品广告海报 AI 生成',
    description: '生成20张产品宣传海报，不同风格和场景。',
    requirements: '1. 20张海报\n2. 4K分辨率\n3. 5种不同风格\n4. 可商用授权',
    terms: '1. 7天交付周期\n2. 一次性交付\n3. 允许3次修改',
    total_amount: 8000,
    currency: 'CNY',
    estimated_delivery_date: daysFromNow(7),
    status: 'PENDING_CLIENT_APPROVAL',
    creator_signed_at: now,
    created_at: now,
    updated_at: now,
  },
  // Contract 7: Active, as creator, with completed milestone
  {
    contract_id: 'contract_007',
    client_user_id: 'client2',
    creator_user_id: 'currentUser',
    title: '智能家居全屋控制系统 UI/UX 设计',
    description: '为高端智能家居系统设计移动端 APP 界面，包含中控屏、灯光、安防等模块。',
    requirements: '1. 现代极简风格\n2. 支持暗色模式\n3. 包含30+个核心页面\n4. 提供交互原型',
    terms: '1. 30天交付周期\n2. 分2个阶段交付\n3. 需提供 Figma 源文件',
    total_amount: 20000,
    currency: 'CNY',
    estimated_delivery_date: daysFromNow(20),
    status: 'ACTIVE',
    client_signed_at: daysAgo(15),
    creator_signed_at: daysAgo(15),
    created_at: daysAgo(16),
    updated_at: daysAgo(1),
  },
];

// Mock Milestones
export const mockMilestones: Record<string, Milestone[]> = {
  contract_002: [
    {
      milestone_id: 'milestone_001',
      contract_id: 'contract_002',
      title: '第一支视频制作',
      description: '完成第一支产品视频的脚本设计和初版制作',
      order_index: 1,
      payment_amount: 4000,
      requires_deliverable: true,
      deliverable_urls: [
        'https://example.com/video1_v1.mp4',
        'https://example.com/video1_source.zip',
      ],
      estimated_completion_date: daysAgo(2),
      status: 'COMPLETED',
      submitted_at: daysAgo(3),
      approved_at: daysAgo(2),
      submission_count: 2,
      completed_at: daysAgo(2),
      created_at: daysAgo(8),
      updated_at: daysAgo(2),
    },
    {
      milestone_id: 'milestone_002',
      contract_id: 'contract_002',
      title: '第二支视频制作',
      description: '完成第二支产品视频制作',
      order_index: 2,
      payment_amount: 4000,
      requires_deliverable: true,
      deliverable_urls: [
        'https://example.com/video2_draft.mp4',
      ],
      estimated_completion_date: daysFromNow(3),
      status: 'REVISION_REQUESTED',
      submitted_at: daysAgo(1),
      revision_feedback: '整体效果不错，但需要调整：\n1. 产品特写镜头需要更清晰\n2. 配乐节奏可以更快一些\n3. 最后的品牌logo展示时间延长到3秒',
      submission_count: 1,
      created_at: daysAgo(8),
      updated_at: daysAgo(1),
    },
    {
      milestone_id: 'milestone_003',
      contract_id: 'contract_002',
      title: '第三支视频制作',
      description: '完成第三支产品视频制作',
      order_index: 3,
      payment_amount: 4000,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(8),
      status: 'NOT_STARTED',
      submission_count: 0,
      created_at: daysAgo(8),
      updated_at: daysAgo(8),
    },
  ],
  contract_003: [
    {
      milestone_id: 'milestone_004',
      contract_id: 'contract_003',
      title: '场景1-2 概念设计',
      description: '完成飞船内部和指挥中心两个场景的概念设计',
      order_index: 1,
      payment_amount: 7200,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(5),
      status: 'IN_PROGRESS',
      submission_count: 0,
      created_at: daysAgo(5),
      updated_at: daysAgo(3),
    },
    {
      milestone_id: 'milestone_005',
      contract_id: 'contract_003',
      title: '场景3-4 概念设计',
      description: '完成外星球表面和空间站两个场景的概念设计',
      order_index: 2,
      payment_amount: 7200,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(9),
      status: 'NOT_STARTED',
      submission_count: 0,
      created_at: daysAgo(5),
      updated_at: daysAgo(5),
    },
    {
      milestone_id: 'milestone_006',
      contract_id: 'contract_003',
      title: '场景5 最终决战场景',
      description: '完成最终决战场景的概念设计和精修',
      order_index: 3,
      payment_amount: 3600,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(12),
      status: 'NOT_STARTED',
      submission_count: 0,
      created_at: daysAgo(5),
      updated_at: daysAgo(5),
    },
  ],
  contract_004: [
    {
      milestone_id: 'milestone_007',
      contract_id: 'contract_004',
      title: '脚本和分镜设计',
      description: '完成宣传片脚本编写和分镜头设计',
      order_index: 1,
      payment_amount: 9000,
      requires_deliverable: true,
      deliverable_urls: ['https://example.com/script.pdf', 'https://example.com/storyboard.pdf'],
      estimated_completion_date: daysAgo(25),
      status: 'COMPLETED',
      submitted_at: daysAgo(26),
      approved_at: daysAgo(25),
      submission_count: 1,
      completed_at: daysAgo(25),
      created_at: daysAgo(32),
      updated_at: daysAgo(25),
    },
    {
      milestone_id: 'milestone_008',
      contract_id: 'contract_004',
      title: '素材制作和初剪',
      description: '完成视频素材制作和初步剪辑',
      order_index: 2,
      payment_amount: 18000,
      requires_deliverable: true,
      deliverable_urls: ['https://example.com/draft_cut.mp4'],
      estimated_completion_date: daysAgo(15),
      status: 'COMPLETED',
      submitted_at: daysAgo(16),
      approved_at: daysAgo(15),
      submission_count: 2,
      completed_at: daysAgo(15),
      created_at: daysAgo(32),
      updated_at: daysAgo(15),
    },
    {
      milestone_id: 'milestone_009',
      contract_id: 'contract_004',
      title: '配音配乐和精剪',
      description: '完成配音配乐制作和视频精剪',
      order_index: 3,
      payment_amount: 9000,
      requires_deliverable: true,
      deliverable_urls: ['https://example.com/final_video.mp4'],
      estimated_completion_date: daysAgo(8),
      status: 'COMPLETED',
      submitted_at: daysAgo(9),
      approved_at: daysAgo(8),
      submission_count: 1,
      completed_at: daysAgo(8),
      created_at: daysAgo(32),
      updated_at: daysAgo(8),
    },
    {
      milestone_id: 'milestone_010',
      contract_id: 'contract_004',
      title: '字幕和最终输出',
      description: '添加中英文字幕，输出多种格式版本',
      order_index: 4,
      payment_amount: 9000,
      requires_deliverable: true,
      deliverable_urls: [
        'https://example.com/final_cn.mp4',
        'https://example.com/final_en.mp4',
        'https://example.com/sources.zip',
      ],
      estimated_completion_date: daysAgo(2),
      status: 'COMPLETED',
      submitted_at: daysAgo(3),
      approved_at: daysAgo(2),
      submission_count: 1,
      completed_at: daysAgo(2),
      created_at: daysAgo(32),
      updated_at: daysAgo(2),
    },
  ],
  contract_001: [
    {
      milestone_id: 'milestone_011',
      contract_id: 'contract_001',
      title: '创意方案和分镜',
      description: '提供创意方案和详细分镜头脚本',
      order_index: 1,
      payment_amount: 8000,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(5),
      status: 'NOT_STARTED',
      submission_count: 0,
      created_at: daysAgo(1),
      updated_at: daysAgo(1),
    },
    {
      milestone_id: 'milestone_012',
      contract_id: 'contract_001',
      title: 'AI 素材生成和初剪',
      description: '使用 Sora 生成视频素材并完成初步剪辑',
      order_index: 2,
      payment_amount: 12000,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(10),
      status: 'NOT_STARTED',
      submission_count: 0,
      created_at: daysAgo(1),
      updated_at: daysAgo(1),
    },
    {
      milestone_id: 'milestone_013',
      contract_id: 'contract_001',
      title: '后期制作和交付',
      description: '完成后期特效、调色、配乐，输出最终版本',
      order_index: 3,
      payment_amount: 5000,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(15),
      status: 'NOT_STARTED',
      submission_count: 0,
      created_at: daysAgo(1),
      updated_at: daysAgo(1),
    },
  ],
  contract_006: [
    {
      milestone_id: 'milestone_014',
      contract_id: 'contract_006',
      title: '海报设计和生成',
      description: '生成20张不同风格的产品海报',
      order_index: 1,
      payment_amount: 8000,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(7),
      status: 'NOT_STARTED',
      submission_count: 0,
      created_at: now,
      updated_at: now,
    },
  ],
  contract_007: [
    {
      milestone_id: 'milestone_015',
      contract_id: 'contract_007',
      title: '第一阶段：核心功能页设计',
      description: '完成首页、设备控制、场景模式等核心页面的 UI 设计',
      order_index: 1,
      payment_amount: 10000,
      requires_deliverable: true,
      deliverable_urls: [
        'https://example.com/smart_home_phase1.fig',
        'https://example.com/prototype_v1.mov'
      ],
      estimated_completion_date: daysAgo(5),
      status: 'COMPLETED',
      submitted_at: daysAgo(6),
      approved_at: daysAgo(5),
      submission_count: 1,
      completed_at: daysAgo(5),
      created_at: daysAgo(15),
      updated_at: daysAgo(5),
    },
    {
      milestone_id: 'milestone_016',
      contract_id: 'contract_007',
      title: '第二阶段：设置与扩展功能',
      description: '完成用户设置、安防监控、能耗统计等其余页面设计',
      order_index: 2,
      payment_amount: 10000,
      requires_deliverable: true,
      deliverable_urls: [],
      estimated_completion_date: daysFromNow(15),
      status: 'IN_PROGRESS',
      submission_count: 0,
      created_at: daysAgo(15),
      updated_at: daysAgo(5),
    },
  ],
};

// Mock Amendments
export const mockAmendments: Record<string, Amendment[]> = {
  contract_002: [
    {
      amendment_id: 'amendment_001',
      contract_id: 'contract_002',
      proposed_by: 'currentUser',
      amendment_type: 'EXTEND_DEADLINE',
      changes: {
        newDeliveryDate: daysFromNow(15),
      },
      status: 'APPROVED',
      reviewed_at: daysAgo(3),
      reviewed_by: 'creator2',
      review_notes: '同意延期，需要更多时间打磨质量',
      created_at: daysAgo(4),
      updated_at: daysAgo(3),
    },
    {
      amendment_id: 'amendment_002',
      contract_id: 'contract_002',
      proposed_by: 'creator2',
      amendment_type: 'UPDATE_PAYMENT',
      changes: {
        newTotalAmount: 15000,
      },
      status: 'PENDING',
      created_at: daysAgo(1),
      updated_at: daysAgo(1),
    },
  ],
  contract_003: [
    {
      amendment_id: 'amendment_003',
      contract_id: 'contract_003',
      proposed_by: 'client1',
      amendment_type: 'ADD_MILESTONE',
      changes: {
        milestone: {
          title: '额外场景设计',
          description: '增加一个外星城市的场景设计',
          order_index: 4,
          payment_amount: 5000,
          requires_deliverable: true,
          estimated_completion_date: daysFromNow(15),
        },
      },
      status: 'PENDING',
      created_at: daysAgo(2),
      updated_at: daysAgo(2),
    },
  ],
};

// Function to get contract with user info
export const getContractWithUsers = (contractId: string): ContractWithUsers | undefined => {
  const contract = mockContracts.find((c) => c.contract_id === contractId);
  if (!contract) return undefined;

  const client = mockUsers[contract.client_user_id];
  const creator = mockUsers[contract.creator_user_id];

  return {
    ...contract,
    client,
    creator,
  };
};

// Function to get all contracts with user info
export const getAllContractsWithUsers = (): ContractWithUsers[] => {
  return mockContracts.map((contract) => ({
    ...contract,
    client: mockUsers[contract.client_user_id],
    creator: mockUsers[contract.creator_user_id],
  }));
};

// Function to get milestones for a contract
export const getMilestonesForContract = (contractId: string): Milestone[] => {
  return mockMilestones[contractId] || [];
};

// Function to get amendments for a contract
export const getAmendmentsForContract = (contractId: string): Amendment[] => {
  return mockAmendments[contractId] || [];
};

// Current user ID for testing
export const CURRENT_USER_ID = 'currentUser';
